import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../../api/api";
import { Routes } from "../../routes";

interface Asset {
  id: string;
  name: string;
  code: string;
}

export const AddSpotPage: React.FC = () => {
  const { patrolId } = useParams<{ patrolId: string }>();
  const navigate = useNavigate();

  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [newSpotLevel, setNewSpotLevel] = useState<number>(0);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const showMessage = (message: string, type: 'success' | 'error' = 'success') => {
    const messageEl = document.createElement('div');
    messageEl.textContent = message;
    messageEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      border-radius: 8px;
      color: white;
      font-weight: 500;
      z-index: 10000;
      background-color: ${type === 'success' ? '#10b981' : '#ef4444'};
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      transform: translateX(100%);
      transition: transform 0.3s ease;
      max-width: 300px;
    `;
    document.body.appendChild(messageEl);

    setTimeout(() => {
      messageEl.style.transform = 'translateX(0)';
    }, 100);

    setTimeout(() => {
      messageEl.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (document.body.contains(messageEl)) {
          document.body.removeChild(messageEl);
        }
      }, 300);
    }, 3000);
  };

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axiosInstance.get(`/api/v1/patrol/asset/index`);
      setAssets(res.data.data || []);
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || "Gagal memuat assets");
      showMessage('Gagal memuat daftar spot', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  const handleAssign = async () => {
    if (!selectedAsset) {
      showMessage('Pilih spot yang akan di-assign', 'error');
      return;
    }

    try {
      const selectedAssetData = assets.find(a => a.id === selectedAsset);
      await axiosInstance.post(`/api/v1/patrol/component/${patrolId}/store`, {
        patrol_asset_id: selectedAsset,
        code: selectedAssetData?.code || "",
      });

      const spotsRes = await axiosInstance.get(
        `/api/v1/patrol/component/${patrolId}/index?per_page=50`
      );
      const spots = spotsRes.data.data || [];
      const newLevel = spots.length;

      setSuccessMessage(`Berhasil menambahkan spot "${selectedAssetData?.name}" di Level ${newLevel}`);
      setNewSpotLevel(newLevel);
      setShowSuccessPopup(true);
      
      setTimeout(() => {
        navigate(`${Routes.PatrolDetailPage}/${patrolId}`);
      }, 3000);

    } catch (err: any) {
      console.error(err);
      showMessage('Gagal assign spot: ' + (err.response?.data?.message || "Unknown error"), 'error');
    }
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    navigate(`${Routes.PatrolDetailPage}/${patrolId}`);
  };

  const mobileBottomPadding = isMobile ? "50px" : "16px";

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      padding: "16px",
      maxWidth: "600px",
      margin: "0 auto",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      backgroundColor: "#f8fafc",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",

      paddingBottom: `calc(16px + ${mobileBottomPadding})`,
    },
    backButton: {
      backgroundColor: "#6b7280",
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      padding: "10px 16px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      transition: "all 0.2s ease",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      marginBottom: "16px",
      width: "fit-content",
      alignSelf: "flex-start",
      flexShrink: 0
    },
    header: {
      textAlign: "center",
      marginBottom: "16px",
      flexShrink: 0
    },
    title: {
      fontSize: "22px",
      fontWeight: "700",
      color: "#1f2937",
      margin: "0 0 6px 0"
    },
    subtitle: {
      fontSize: "14px",
      color: "#6b7280",
      margin: 0
    },
    contentWrapper: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      marginBottom: "16px",
      minHeight: 0
    },
    selectedCount: {
      textAlign: "center",
      fontSize: "13px",
      color: "#059669",
      fontWeight: "500",
      marginBottom: "12px",
      flexShrink: 0,
      padding: "8px 12px",
      backgroundColor: "#f0f9ff",
      borderRadius: "8px",
      border: "1px solid #d1fae5"
    },
    assetsContainer: {
      backgroundColor: "white",
      borderRadius: "10px",
      border: "1px solid #e5e7eb",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minHeight: 0
    },
    assetsScrollContainer: {
      flex: 1,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column"
    },
    assetCard: {
      padding: "12px 16px",
      borderBottom: "1px solid #f3f4f6",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexShrink: 0,
      minHeight: "60px"
    },
    selectedAssetCard: {
      backgroundColor: "#f0f9ff",
      borderLeft: "3px solid #3b82f6"
    },
    radio: {
      width: "16px",
      height: "16px",
      accentColor: "#3b82f6",
      flexShrink: 0
    },
    assetInfo: {
      flex: 1,
      minWidth: 0
    },
    assetName: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#1f2937",
      margin: "0 0 2px 0",
      lineHeight: "1.3",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },
    assetCode: {
      fontSize: "12px",
      color: "#6b7280",
      margin: 0,
      fontFamily: "monospace"
    },
    selectedBadge: {
      padding: "3px 8px",
      backgroundColor: "#10b981",
      color: "white",
      borderRadius: "10px",
      fontSize: "11px",
      fontWeight: "600",
      flexShrink: 0
    },
    buttonContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      flexShrink: 0,
      backgroundColor: "#f8fafc",
      padding: "16px",
      borderTop: "1px solid #e5e7eb",
      position: "sticky",
      bottom: "0",
      marginBottom: isMobile ? "60px" : "0",
      paddingBottom: `calc(16px + ${isMobile ? '30px' : '0px'})`,
    },
    buttonGroup: {
      display: "flex",
      gap: "10px",
      justifyContent: "center"
    },
    assignButton: {
      padding: "14px 20px",
      backgroundColor: "#059669",
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "16px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 4px rgba(5, 150, 105, 0.3)",
      flex: 1
    },
    disabledButton: {
      backgroundColor: "#9ca3af",
      cursor: "not-allowed",
      boxShadow: "none"
    },
    cancelButton: {
      padding: "14px 20px",
      backgroundColor: "#6b7280",
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "16px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      transition: "all 0.2s ease",
      flex: 1
    },
    loadingState: {
      textAlign: "center",
      padding: "40px 20px",
      color: "#6b7280",
      fontSize: "14px",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center"
    },
    errorState: {
      textAlign: "center",
      padding: "40px 20px",
      color: "#dc2626",
      fontSize: "14px",
      backgroundColor: "#fef2f2",
      borderRadius: "8px",
      border: "1px solid #fecaca",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center"
    },
    emptyState: {
      textAlign: "center",
      padding: "40px 20px",
      color: "#6b7280",
      fontSize: "14px",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center"
    }
  };

  const successPopupStyles: { [key: string]: React.CSSProperties } = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: "16px",
      zIndex: 1000
    },
    popup: {
      backgroundColor: 'white',
      padding: "20px",
      borderRadius: "12px",
      width: "100%",
      maxWidth: "400px",
      boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
      textAlign: "center",
      position: "relative"
    },
    successIcon: {
      fontSize: "40px",
      marginBottom: "12px",
      color: "#10b981"
    },
    popupTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#1f2937",
      margin: "0 0 10px 0"
    },
    popupMessage: {
      fontSize: "14px",
      color: "#4b5563",
      lineHeight: "1.4",
      margin: "0 0 16px 0"
    },
    levelBadge: {
      display: "inline-block",
      backgroundColor: "#3b82f6",
      color: "white",
      padding: "6px 12px",
      borderRadius: "16px",
      fontSize: "14px",
      fontWeight: "600",
      margin: "8px 0"
    },
    progressBar: {
      height: "3px",
      backgroundColor: "#e5e7eb",
      borderRadius: "2px",
      overflow: "hidden",
      marginTop: "12px"
    },
    progressFill: {
      height: "100%",
      backgroundColor: "#10b981",
      borderRadius: "2px",
      animation: "shrink 3s linear forwards"
    },
    closeButton: {
      position: "absolute",
      top: "12px",
      right: "12px",
      background: "none",
      border: "none",
      fontSize: "16px",
      cursor: "pointer",
      color: "#6b7280",
      padding: "4px",
      borderRadius: "4px",
      transition: "color 0.2s ease"
    },
    autoCloseText: {
      fontSize: "12px",
      color: "#6b7280",
      fontStyle: "italic",
      marginTop: "8px"
    }
  };

  if (loading) return (
    <div style={styles.container}>
      <div style={styles.loadingState}>
        <div style={{ fontSize: "36px", marginBottom: "12px" }}></div>
        Memuat daftar spot...
      </div>
    </div>
  );

  if (error) return (
    <div style={styles.container}>
      <div style={styles.errorState}>
        <div style={{ fontSize: "36px", marginBottom: "12px" }}></div>
        {error}
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <button 
        onClick={() => navigate(`${Routes.PatrolDetailPage}/${patrolId}`)}
        style={styles.backButton}
      >
        <span>←</span>
      </button>

      <div style={styles.header}>
        <h1 style={styles.title}>Assign Spot Patrol</h1>
        <p style={styles.subtitle}>
          Pilih spot yang ingin ditambahkan ke jadwal patrol
        </p>
      </div>

      <div style={styles.contentWrapper}>
        {selectedAsset && (
          <div style={styles.selectedCount}>
            1 spot dipilih
          </div>
        )}

        <div style={styles.assetsContainer}>
          <div style={styles.assetsScrollContainer}>
            {assets.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: "36px", marginBottom: "12px" }}>📭</div>
                Tidak ada spot yang tersedia
              </div>
            ) : (
              assets.map((asset) => (
                <div
                  key={asset.id}
                  style={{
                    ...styles.assetCard,
                    ...(selectedAsset === asset.id ? styles.selectedAssetCard : {})
                  }}
                  onClick={() => setSelectedAsset(asset.id)}
                >
                  <input
                    type="radio"
                    name="spot"
                    value={asset.id}
                    checked={selectedAsset === asset.id}
                    onChange={() => setSelectedAsset(asset.id)}
                    style={styles.radio}
                  />
                  <div style={styles.assetInfo}>
                    <h3 style={styles.assetName}>{asset.name}</h3>
                    <p style={styles.assetCode}>
                      {asset.code}
                    </p>
                  </div>
                  {selectedAsset === asset.id && (
                    <div style={styles.selectedBadge}>
                      Dipilih
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div style={styles.buttonContainer}>
        <div style={styles.buttonGroup}>
          <button
            onClick={() => navigate(`${Routes.PatrolDetailPage}/${patrolId}`)}
            style={styles.cancelButton}
          >
            Batal
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedAsset}
            style={{
              ...styles.assignButton,
              ...(!selectedAsset ? styles.disabledButton : {})
            }}
          >
            
            Assign Spot
          </button>
        </div>

        {!selectedAsset && (
          <div style={{
            textAlign: "center",
            fontSize: "13px",
            color: "#6b7280",
            fontStyle: "italic",
            padding: "8px"
          }}>
            Pilih salah satu spot dari daftar di atas untuk melanjutkan
          </div>
        )}
      </div>

      {showSuccessPopup && (
        <div 
          style={successPopupStyles.overlay}
          onClick={handleCloseSuccessPopup}
        >
          <div 
            style={successPopupStyles.popup}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              style={successPopupStyles.closeButton}
              onClick={handleCloseSuccessPopup}
            >
              ✕
            </button>
            <div style={successPopupStyles.successIcon}>✅</div>
            <h3 style={successPopupStyles.popupTitle}>Berhasil!</h3>
            <p style={successPopupStyles.popupMessage}>{successMessage}</p>
            <div style={successPopupStyles.levelBadge}>
              Level {newSpotLevel}
            </div>
            <div style={successPopupStyles.progressBar}>
              <div style={successPopupStyles.progressFill}></div>
            </div>
            <div style={successPopupStyles.autoCloseText}>
              Otomatis kembali ke halaman patrol dalam 3 detik...
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const spinnerStyles = document.createElement('style')
spinnerStyles.innerHTML = `
  @keyframes shrink {
    0% { width: 100%; }
    100% { width: 0%; }
  }
`

document.head.appendChild(spinnerStyles)